import React, { useState } from "react";
import classnames from "classnames";
import { QueryKey, useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";

import { FilterIcon } from "fe-shared/src/components/Icons";

import BusinessRole from "fe-shared/src/types/domain/BusinessRole";
import useDialog from "fe-shared/src/hooks/useDialog";
import { Button, Loader } from "fe-shared/src/components";
import { ApiError } from "fe-shared/src/types/common";

import { CreateInvoiceDialog } from "..";
import {
  InvoiceCreateInfo,
  InvoiceItemType,
  InvoiceTransaction,
} from "../../types";

import * as api from "./modules/api";

import styles from "./CreateInvoiceButton.module.scss";

interface Props {
  isDisabled?: boolean;
  canCreateInvoice: boolean;
  businessRoleCode: BusinessRole;
  displayName: string;
  isWithTransactions?: boolean;
  getItemNumbers: () => Promise<string[]>;
  listQueryKey: QueryKey;
  isShowInvoiceableApplied: boolean;
  setIsShowInvoiceable: () => void;
}

const CreateInvoiceButton = ({
  isDisabled,
  canCreateInvoice,
  businessRoleCode,
  displayName,
  isWithTransactions = false,
  getItemNumbers,
  listQueryKey,
  isShowInvoiceableApplied,
  setIsShowInvoiceable,
}: Props) => {
  const {
    isDialogActive,
    openDialog,
    closeDialog,
    isDialogRendered,
  } = useDialog();

  const { t } = useTranslation();
  const [areTransactionsLoading, setAreTransactionsLoading] = useState(false);

  const [getInvoiceInfo, { isLoading, data }] = useMutation<
    InvoiceCreateInfo<InvoiceTransaction>,
    AxiosError<ApiError>,
    api.CreateInvoiceInfoParams
  >(api.getCreateInvoiceInfo, {
    throwOnError: true,
    onSuccess: () => {
      openDialog();
    },
  });

  const onClickHandler = async () => {
    try {
      setAreTransactionsLoading(true);
      const transactionNumbers = await getItemNumbers();

      setAreTransactionsLoading(false);
      await getInvoiceInfo({
        transactionNumbers,
        invoiceTarget: isWithTransactions
          ? InvoiceItemType.Transaction
          : InvoiceItemType.Transfer,
      });
    } catch {
      setAreTransactionsLoading(false);
    }
  };

  return (
    <>
      {canCreateInvoice && (
        <div className={styles.createInvoiceWrapper}>
          <Button
            size="medium"
            variant="outlined"
            id="only-invoiceable-filter-button"
            iconComponent={FilterIcon}
            className={classnames(styles.showInvoiceableButton, {
              [styles.showInvoiceableButtonActive]: isShowInvoiceableApplied,
            })}
            onClick={setIsShowInvoiceable}
          >
            {t("transactions.showInvoiceableBtn")}
          </Button>
          {(areTransactionsLoading || isLoading) && (
            <Loader className={styles.inlineLoader} fontSize={18} />
          )}
          <Button
            size="medium"
            onClick={onClickHandler}
            id="transaction-create-invoice"
            disabled={areTransactionsLoading || isLoading || isDisabled}
          >
            {t("transactions.createInvoiceBtn")}
          </Button>
        </div>
      )}
      {isDialogRendered && (
        <CreateInvoiceDialog
          listQueryKey={listQueryKey}
          isDialogActive={isDialogActive}
          closeDialog={closeDialog}
          displayName={displayName}
          role={businessRoleCode}
          invoiceInfo={data!}
        />
      )}
    </>
  );
};

export default CreateInvoiceButton;
